import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import {
  cn,
  currentMonthRange,
  monthRange,
  nextMonthRange,
  previousMonthRange,
  rangeEquals,
  rangeLabel,
  type DateRange,
} from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface MonthPeriodFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** Mostrar atalhos "Mês anterior / atual / próximo". Default: true. */
  showMonthPresets?: boolean;
  className?: string;
}

/**
 * Filtro de período com presets de mês (anterior / atual / próximo) + intervalo livre.
 * Default no consumidor: chamar `currentMonthRange()` ao montar a página.
 */
export function MonthPeriodFilter({
  value,
  onChange,
  showMonthPresets = true,
  className,
}: MonthPeriodFilterProps) {
  const presets = showMonthPresets ? [
    { label: 'Mês anterior', range: previousMonthRange() },
    { label: 'Mês atual', range: currentMonthRange() },
    { label: 'Próximo mês', range: nextMonthRange() },
  ] : [];

  const matchesPreset = presets.some((p) => rangeEquals(p.range, value));
  const isCustom = !matchesPreset;

  function navMonth(delta: -1 | 1) {
    // Se o range atual cobre exatamente um mês, navega mês a mês a partir dele.
    // Caso contrário, parte do mês corrente.
    const [fy, fm, fd] = value.from.split('-').map(Number);
    const [ty, tm, td] = value.to.split('-').map(Number);
    const lastDayOfMonth = new Date(ty, tm, 0).getDate();
    const fullMonth = fy === ty && fm === tm && fd === 1 && td === lastDayOfMonth;
    if (fullMonth) {
      onChange(monthRange(fy, fm - 1 + delta));
    } else {
      const t = new Date();
      onChange(monthRange(t.getFullYear(), t.getMonth() + delta));
    }
  }

  return (
    <div className={cn('bg-card border rounded-lg p-3 space-y-3', className)}>
      <div className="flex flex-wrap items-end gap-3">
        {showMonthPresets && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => navMonth(-1)}
              className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-input bg-background hover:bg-accent"
              aria-label="Mês anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex rounded-md border border-input overflow-hidden">
              {presets.map((p) => {
                const active = rangeEquals(p.range, value);
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => onChange(p.range)}
                    className={cn(
                      'px-3 h-9 text-sm transition-colors',
                      active
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'bg-background hover:bg-accent text-muted-foreground',
                    )}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => navMonth(1)}
              className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-input bg-background hover:bg-accent"
              aria-label="Próximo mês"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="flex items-end gap-2">
          <div className="space-y-1.5">
            <Label className="text-xs">De</Label>
            <Input
              type="date"
              value={value.from}
              onChange={(e) => onChange({ ...value, from: e.target.value })}
              className="w-[150px]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Até</Label>
            <Input
              type="date"
              value={value.to}
              onChange={(e) => onChange({ ...value, to: e.target.value })}
              className="w-[150px]"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto text-sm">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{rangeLabel(value)}</span>
          {isCustom && (
            <span className="text-xs text-muted-foreground bg-muted rounded px-1.5 py-0.5">
              período personalizado
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
